import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  MagnifyingGlass,
  WarningCircle,
  CheckCircle,
  Calendar as CalendarIcon,
  Upload,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSites } from "@/lib/sites";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fetchDocumentsOverview, updateTruck } from "@/lib/api";
import { api } from "@/lib/api";

const RENTAL_LABEL = {
  own: "NY TRANS",
  "loc-eco": "Loc Eco",
  europcar: "Europcar",
  hertz: "Hertz",
  ada: "ADA",
  loa: "LOA (Leasing)",
  autre: "Autre",
};

const STATUS_META = {
  missing: { label: "MANQUANT", bg: "#1f1f1f", fg: "#FFFFFF", priority: 0 },
  expired: { label: "EXPIRÉ", bg: "#DC2626", fg: "#FFFFFF", priority: 1 },
  urgent: { label: "URGENT", bg: "#F97316", fg: "#FFFFFF", priority: 2 },
  warning: { label: "ALERTE", bg: "#F59E0B", fg: "#1f1f1f", priority: 3 },
  ok: { label: "OK", bg: "#10B981", fg: "#FFFFFF", priority: 4 },
  closed: { label: "RÉSILIÉ", bg: "#71717A", fg: "#FFFFFF", priority: 4 },
};

const SLOT_LABELS = {
  insurance: "Assurance",
  technical_check: "Contrôle technique",
  transport_license: "Licence transport",
  pollution_control: "Contrôle pollution",
  carte_grise: "Carte grise",
};

export default function DocumentsPage() {
  const { sites: sitesList } = useSites();
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("issues"); // issues | all | missing | expired | urgent | warning
  const [slotFilter, setSlotFilter] = useState("ALL"); // ALL | insurance | technical_check | transport_license
  const [ownerFilter, setOwnerFilter] = useState("all"); // all | rental_type key
  const [owners, setOwners] = useState([]);

  const ownerMap = useMemo(() => {
    const m = {};
    for (const o of owners) m[o.key] = o.label;
    return m;
  }, [owners]);

  useEffect(() => {
    api
      .get("/owners")
      .then((r) => setOwners(r.data || []))
      .catch(() => {});
  }, []);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchDocumentsOverview(
        siteFilter === "ALL" ? undefined : siteFilter
      );
      setData(res);
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [siteFilter]);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const handleCloseInsurance = async (truckId, plate, e) => {
    e.stopPropagation();
    if (!window.confirm(`Confirmer que l'assurance de ${plate} a bien été résiliée ?`)) return;
    try {
      await updateTruck(truckId, {
        insurance_closed: true,
        insurance_closed_at: new Date().toISOString(),
      });
      toast.success(`Assurance résiliée pour ${plate}`);
      await load({ silent: true });
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return data.rows.filter((r) => {
      // Search filter
      if (s && !r.license_plate.toLowerCase().includes(s) && !(r.brand || "").toLowerCase().includes(s)) {
        return false;
      }
      // Owner filter (applies to ALL rows, including rendu alerts)
      if (ownerFilter !== "all") {
        const rt = r.rental_type || "own";
        if (rt !== ownerFilter) return false;
      }
      // "Rendu awaiting insurance closure" rows always bypass status/slot filters (they are urgent reminders)
      if (r.rendu_needs_closure) return true;
      // Status filter
      if (statusFilter === "issues" && r.worst_level === "ok") return false;
      if (statusFilter === "missing" && !r.slots.some((sl) => sl.status === "missing")) return false;
      if (statusFilter === "expired" && !r.slots.some((sl) => sl.status === "expired")) return false;
      if (statusFilter === "urgent" && !r.slots.some((sl) => sl.status === "urgent")) return false;
      if (statusFilter === "warning" && !r.slots.some((sl) => sl.status === "warning")) return false;
      // Slot filter
      if (slotFilter !== "ALL") {
        const slot = r.slots.find((sl) => sl.key === slotFilter);
        if (!slot || slot.status === "ok") return false;
      }
      return true;
    });
  }, [data.rows, search, statusFilter, slotFilter, ownerFilter]);

  const summary = data.summary || {};

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <FileText size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Suivi documents
          </h1>
          <div className="ny-divider w-32 mt-2" />
          <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
            Quel camion manque quel document — assurance, contrôle technique, licence transport, contrôle pollution, carte grise
          </p>
        </div>
      </div>

      {/* Rendu insurance closure alert banner */}
      {summary.rendu_pending_insurance > 0 && (
        <div
          className="mb-4 border-2 border-red-600 bg-red-50 px-4 py-3 flex items-start gap-3"
          data-testid="rendu-insurance-alert"
        >
          <WarningCircle size={22} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-bold text-red-900">
              Rappel · Assurance à résilier
            </div>
            <div className="text-sm text-red-800 mt-0.5">
              <strong>{summary.rendu_pending_insurance}</strong> camion(s) rendu(s) pour lesquels vous n&apos;avez
              pas encore précisé que l&apos;assurance a été coupée. Utilisez le bouton{" "}
              <span className="font-bold uppercase">Résilier</span> ci-dessous pour éviter tout coût inutile.
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="docs-summary">
        <SummaryCard
          label="Camions"
          value={summary.total_trucks ?? "—"}
          sub={`${summary.trucks_with_issues ?? 0} avec problème`}
          tone="default"
          testId="sum-total"
        />
        <SummaryCard
          label="Manquants"
          value={summary.missing_docs ?? 0}
          sub="document(s) non uploadé(s)"
          tone="missing"
          testId="sum-missing"
          onClick={() => setStatusFilter("missing")}
        />
        <SummaryCard
          label="Expirés"
          value={summary.expired_docs ?? 0}
          sub="à renouveler maintenant"
          tone="expired"
          testId="sum-expired"
          onClick={() => setStatusFilter("expired")}
        />
        <SummaryCard
          label="Urgent ≤7j"
          value={summary.urgent_docs ?? 0}
          sub="expirent cette semaine"
          tone="urgent"
          testId="sum-urgent"
          onClick={() => setStatusFilter("urgent")}
        />
        <SummaryCard
          label="Alerte ≤30j"
          value={summary.warning_docs ?? 0}
          sub="expirent ce mois-ci"
          tone="warning"
          testId="sum-warning"
          onClick={() => setStatusFilter("warning")}
        />
      </div>

      {/* Filters */}
      <div className="border border-zinc-900 bg-white mb-4 px-3 py-2 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par plaque / marque…"
            className="pl-7 rounded-none border-zinc-300 h-9 text-sm"
            data-testid="docs-search"
          />
        </div>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="rounded-none border-zinc-300 h-9 w-32" data-testid="docs-site-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="ALL">Tous sites</SelectItem>
            {sitesList.map((s) => (
              <SelectItem key={s.code} value={s.code}>
                {s.name ? `${s.code} · ${s.name}` : s.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none border-zinc-300 h-9 w-44" data-testid="docs-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="issues">À traiter</SelectItem>
            <SelectItem value="all">Tout afficher</SelectItem>
            <SelectItem value="missing">Manquant uniquement</SelectItem>
            <SelectItem value="expired">Expiré uniquement</SelectItem>
            <SelectItem value="urgent">Urgent ≤7j</SelectItem>
            <SelectItem value="warning">Alerte ≤30j</SelectItem>
          </SelectContent>
        </Select>
        <Select value={slotFilter} onValueChange={setSlotFilter}>
          <SelectTrigger className="rounded-none border-zinc-300 h-9 w-52" data-testid="docs-slot-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="ALL">Tous documents</SelectItem>
            <SelectItem value="insurance">Assurance</SelectItem>
            <SelectItem value="technical_check">Contrôle technique</SelectItem>
            <SelectItem value="transport_license">Licence transport</SelectItem>
            <SelectItem value="pollution_control">Contrôle pollution</SelectItem>
            <SelectItem value="carte_grise">Carte grise</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 mono">
          {filteredRows.length} / {data.rows.length} camion(s)
        </div>
      </div>

      {/* Owner (Propriété) chips */}
      <div
        className="border border-zinc-900 bg-white mb-4 px-3 py-2 flex flex-wrap items-center gap-1.5"
        data-testid="docs-owner-filter-row"
      >
        <div className="label-tag self-center mr-2">Propriété</div>
        <button
          type="button"
          onClick={() => setOwnerFilter("all")}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-none ${
            ownerFilter === "all"
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
          }`}
          data-testid="docs-owner-all"
        >
          Tous
        </button>
        {(() => {
          // Group owners by lower-cased label so legacy 'autre' + new custom labels merge
          const groups = {};
          for (const o of owners) {
            const k = (o.label || o.key).trim().toLowerCase();
            if (!groups[k]) groups[k] = { label: o.label, keys: [] };
            groups[k].keys.push(o.key);
          }
          for (const r of data.rows) {
            const rt = r.rental_type || "own";
            const lbl =
              rt === "autre" && r.rental_type_custom
                ? r.rental_type_custom
                : ownerMap[rt] || RENTAL_LABEL[rt] || rt;
            const k = (lbl || rt).trim().toLowerCase();
            if (!groups[k]) groups[k] = { label: lbl, keys: [] };
            if (!groups[k].keys.includes(rt)) groups[k].keys.push(rt);
          }
          return Object.values(groups)
            .map((g) => {
              const groupRows = data.rows.filter((r) =>
                g.keys.includes(r.rental_type || "own")
              );
              // Only count trucks that have at least one document issue (exclude fully-OK ones)
              const issues = groupRows.filter((r) => r.worst_level !== "ok").length;
              return { ...g, count: groupRows.length, issues };
            })
            .filter((g) => g.count > 0)
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((g) => {
              const isActive = g.keys.includes(ownerFilter);
              return (
                <button
                  key={g.label}
                  type="button"
                  onClick={() => setOwnerFilter(isActive ? "all" : g.keys[0])}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 transition-none ${
                    isActive
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                  }`}
                  data-testid={`docs-owner-${g.keys[0]}`}
                  title={
                    g.issues > 0
                      ? `${g.count} camion(s) · ${g.issues} avec problème`
                      : `${g.count} camion(s) · tous OK`
                  }
                >
                  {g.label}
                  <span
                    className={`px-1.5 text-[10px] mono ${
                      isActive
                        ? "bg-white text-zinc-900"
                        : "bg-zinc-200 text-zinc-700"
                    }`}
                  >
                    {g.count}
                  </span>
                  {g.issues > 0 && (
                    <span
                      className="px-1.5 text-[10px] mono bg-red-600 text-white"
                      title={`${g.issues} avec problème`}
                    >
                      ⚠ {g.issues}
                    </span>
                  )}
                </button>
              );
            });
        })()}
      </div>

      {/* Table */}
      <div className="border border-zinc-900 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="docs-table">
          <thead className="bg-zinc-900 text-white">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold">Plaque</th>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold">Marque · Site</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold !text-[var(--ny-gold-soft)]">Assurance</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold !text-[var(--ny-gold-soft)]">Contrôle technique</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold !text-[var(--ny-gold-soft)]">Licence transport</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold !text-[var(--ny-gold-soft)]">Contrôle pollution</th>
              <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-bold !text-[var(--ny-gold-soft)]">Carte grise</th>
              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest font-bold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center text-xs text-zinc-500 py-6 uppercase tracking-widest">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-xs text-zinc-500 py-10">
                  <CheckCircle size={28} weight="bold" className="mx-auto mb-2 text-emerald-600" />
                  <div className="uppercase tracking-widest font-bold">Tout est en règle</div>
                  <div className="mt-1">Aucun document à traiter selon les filtres actuels.</div>
                </td>
              </tr>
            )}
            {!loading && filteredRows.map((r) => (
              <tr
                key={r.truck_id}
                className={`border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer ${
                  r.rendu_needs_closure ? "bg-red-50 hover:bg-red-100" : ""
                }`}
                onClick={() => navigate(`/trucks/${r.truck_id}`)}
                data-testid={`docs-row-${r.truck_id}`}
              >
                <td className="px-3 py-2 font-bold mono uppercase">
                  {r.license_plate}
                  {r.red_tag && (
                    <span className="ml-1 inline-block px-1 py-0.5 text-[8px] font-bold bg-red-600 text-white align-middle">RED</span>
                  )}
                  {r.rendu_needs_closure && (
                    <span
                      className="ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 text-[8px] font-bold bg-red-600 text-white align-middle uppercase tracking-widest"
                      data-testid={`rendu-badge-${r.truck_id}`}
                    >
                      <WarningCircle size={9} weight="fill" /> Assurance
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-600">
                  {(r.brand || "—")} · {r.site || "—"}
                  {r.status && r.status !== "actif" && (
                    <span className="ml-1 text-[9px] font-bold text-amber-700">[{r.status.toUpperCase()}]</span>
                  )}
                  {r.rendu_needs_closure && (
                    <div className="text-[10px] normal-case tracking-normal text-red-700 font-bold mt-0.5">
                      ⚠ Assurance non résiliée — pensez à couper
                    </div>
                  )}
                </td>
                {r.slots.map((slot) => (
                  <td key={slot.key} className="px-3 py-2 text-center">
                    <SlotBadge slot={slot} />
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex flex-col items-end gap-1">
                    {r.rendu_needs_closure && (
                      <button
                        onClick={(e) => handleCloseInsurance(r.truck_id, r.license_plate, e)}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1"
                        data-testid={`close-insurance-${r.truck_id}`}
                      >
                        <CheckCircle size={11} weight="bold" /> Résilier
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/trucks/${r.truck_id}`);
                      }}
                      className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1"
                      data-testid={`docs-open-${r.truck_id}`}
                    >
                      <Upload size={11} weight="bold" /> Gérer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest" data-testid="docs-legend">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 inline-block" style={{ background: m.bg }} />
            <span className="font-bold">{m.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function SlotBadge({ slot }) {
  const meta = STATUS_META[slot.status] || STATUS_META.missing;
  return (
    <div className="inline-flex flex-col items-center gap-0.5 min-w-[88px]">
      <span
        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
        style={{ background: meta.bg, color: meta.fg }}
        data-testid={`slot-${slot.key}-${slot.status}`}
      >
        {meta.label}
      </span>
      <span className="text-[10px] text-zinc-500 mono inline-flex items-center gap-0.5">
        {slot.expiry ? (
          <>
            <CalendarIcon size={9} weight="bold" />
            {slot.expiry}
          </>
        ) : (
          "—"
        )}
      </span>
      {slot.days_left !== null && slot.days_left !== undefined && slot.status !== "ok" && (
        <span
          className="text-[9px] font-bold mono"
          style={{
            color: slot.days_left < 0 ? "#DC2626" : slot.days_left <= 7 ? "#F97316" : "#B45309",
          }}
        >
          {slot.days_left < 0
            ? `Il y a ${Math.abs(slot.days_left)}j`
            : slot.days_left === 0
            ? "Aujourd'hui"
            : `Dans ${slot.days_left}j`}
        </span>
      )}
      {slot.has_file && slot.status !== "missing" && (
        <span className="text-[8px] uppercase text-emerald-700">📎 fichier</span>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, tone = "default", testId, onClick }) {
  const toneClass =
    tone === "missing"
      ? "border-zinc-900 bg-zinc-900 text-white"
      : tone === "expired"
      ? "border-red-600 bg-red-50 text-red-900"
      : tone === "urgent"
      ? "border-orange-500 bg-orange-50 text-orange-900"
      : tone === "warning"
      ? "border-amber-500 bg-amber-50 text-amber-900"
      : "border-zinc-300 bg-white text-zinc-900";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left border ${toneClass} px-3 py-3 ${onClick ? "hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ny-gold)] cursor-pointer" : "cursor-default"}`}
      data-testid={testId}
    >
      <div className="label-tag !text-current opacity-70">{label}</div>
      <div className="text-3xl font-black mono mt-1 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">{sub}</div>
    </button>
  );
}
